import React, { useCallback } from "react";
import { shallowEqual } from "react-redux";
import groupBy from "lodash/groupBy";
import Container from "../../../../../../../../shared-components/css-modules/lexus/atoms/Container";
import Text from "../../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Swatch from "../../../../../../../../shared-components/css-modules/lexus/atoms/Swatch";
import Button from "../../../../../../../../shared-components/css-modules/lexus/atoms/Button";
import a11y from "../../../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import styles from "./Colour.module.scss";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../../../redux/store";
import CarDataConsolidator from "../../../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { setColour } from "../../../../../../../redux/slices/CarConfigBasketSlice";
import Price from "../../../../../common/price/Price";
import { trackColorSelect } from "../../../../../../../utils/tracking";
import type { ColourType } from "../../../../../../../../../../common-deprecated/types/CommonTypes";
import { showAccessoriesRate } from "../../../../../../../utils/financeInsuranceRate";
import { getColourPriceRange } from "../../../../../../../utils/colourUtils";
import { CarConfigConfigureSectionsLbx } from "../../../../../../../utils/types/SectionTypesLbx";
import SubSectionWrapper from "../SubSectionWrapper";
import baseSectionStyles from "../../../../../common/section-wrapper/SectionWrapper.module.scss";
import type { SectionWrapperPropagationProps } from "../../../../../common/section-wrapper/SectionWrapper";
import { groupColoursBasedOnPack } from "../../../../../../../utils/packUtils";
import { scrollToTarget } from "../../../../../../../../../../common-deprecated/utils/scrollUtils";
import SpinContainer from "../../../../../common/spin/SpinContainer";
import { getCarDBAssetUrl } from "../../../../../../../../../shared-logic/utils/imageUtils";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../../../utils/labelUtils";
import SliderWrapper from "../../../../../common/spin/sliderWrapper/SliderWrapper";
import Badge from "../../../../../../../../shared-components/css-modules/lexus/atoms/Badge";
import { setActiveSpin, SpinnerType } from "../../../../../../../redux/slices/CarConfigSpinNavigationSlice";

const Colour: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }): JSX.Element => {
    const dispatch = useCarConfigDispatch();
    const colours = useCarConfigSelector(
        (state) => CarDataConsolidator.getColours(state.items.colours, state.basket),
        shallowEqual,
    );
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const coloursInGroups = groupColoursBasedOnPack(groupBy(colours, "type"), packs);
    const activeColour = useCarConfigSelector((state) => state.basket.colour);
    const activeSpinner = useCarConfigSelector((state) => state.carConfigSpinNavigation.activeSpin);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);

    const onColourClick = useCallback(
        (colour: ColourType) => () => {
            trackColorSelect();
            dispatch(setColour({ colour }));
            if (activeSpinner !== SpinnerType.Car) {
                dispatch(setActiveSpin(SpinnerType.Car));
            }
        },
        [dispatch, activeSpinner],
    );

    const navigationLabel = useCarConfigLabel("carConfigA11yNavColour");

    return (
        <SubSectionWrapper
            sectionId={CarConfigConfigureSectionsLbx.AtmosphereColor}
            sectionRef={sectionRefs.get(CarConfigConfigureSectionsLbx.AtmosphereColor)}
        >
            <Container variant="full-width">
                <div className={styles.wrapper}>
                    <SpinContainer settings={{ showOnMobile: false, bgColour: "FFFFFF" }} />
                    <SliderWrapper />
                    <div className={styles.configure}>
                        <Text tag="h3" variant="apheleia-h4" className={styles.title}>
                            <CarConfigLabel label="carConfigLbxExteriorColour" />
                        </Text>
                        <nav className={baseSectionStyles["group-wrapper"]} aria-label={navigationLabel}>
                            {Object.entries(coloursInGroups).map(([groupPackId, groupPacks]) => {
                                return (
                                    <div
                                        key={groupPackId}
                                        className={
                                            groupPackId !== "standard" ? baseSectionStyles["pack-group-option"] : ""
                                        }
                                    >
                                        {groupPackId !== "standard" && (
                                            <div className={baseSectionStyles["pack-group-option-header"]}>
                                                <CarConfigLabel
                                                    label="carConfigPartOfPack"
                                                    params={{
                                                        pack: packs.find((pack) => pack.shortId === groupPackId)?.name,
                                                    }}
                                                />
                                                <Button
                                                    className={baseSectionStyles["pack-group-button"]}
                                                    variant="btn-text"
                                                    onClick={() => scrollToTarget(CarConfigConfigureSectionsLbx.Packs)}
                                                >
                                                    <CarConfigLabel label="carConfigViewPack" />
                                                </Button>
                                            </div>
                                        )}

                                        {Object.entries(groupPacks).map(([group, groupColours]) => {
                                            const [lowPrice, highPrice] = getColourPriceRange(groupColours);

                                            const selectedItemInGroup = groupColours.find(
                                                ({ id }) => activeColour.id === id,
                                            );

                                            let renderedPrice;
                                            if ((lowPrice.price?.discount ?? 0) === (highPrice.price?.discount ?? 0)) {
                                                if ((lowPrice.price?.discount ?? 0) === 0) {
                                                    renderedPrice = (
                                                        <Price
                                                            price={lowPrice.price}
                                                            forceCashPrice={
                                                                !showAccessoriesRate(commonSettings, carConfigSettings)
                                                            }
                                                            showZeroPrice
                                                        />
                                                    );
                                                } else {
                                                    renderedPrice = (
                                                        <>
                                                            <Price
                                                                price={lowPrice.price}
                                                                forceCashPrice={
                                                                    !showAccessoriesRate(
                                                                        commonSettings,
                                                                        carConfigSettings,
                                                                    )
                                                                }
                                                            />
                                                        </>
                                                    );
                                                }
                                            } else if ((lowPrice.price?.discount ?? 0) === 0) {
                                                renderedPrice = (
                                                    <>
                                                        <Price
                                                            price={lowPrice.price}
                                                            forceCashPrice={
                                                                !showAccessoriesRate(commonSettings, carConfigSettings)
                                                            }
                                                            showZeroPrice
                                                        />{" "}
                                                        -{" "}
                                                        <Price
                                                            price={highPrice.price}
                                                            forceCashPrice={
                                                                !showAccessoriesRate(commonSettings, carConfigSettings)
                                                            }
                                                        />
                                                    </>
                                                );
                                            } else {
                                                renderedPrice = (
                                                    <>
                                                        <Price
                                                            price={lowPrice.price}
                                                            forceCashPrice={
                                                                !showAccessoriesRate(commonSettings, carConfigSettings)
                                                            }
                                                        />{" "}
                                                        -{" "}
                                                        <Price
                                                            price={highPrice.price}
                                                            forceCashPrice={
                                                                !showAccessoriesRate(commonSettings, carConfigSettings)
                                                            }
                                                        />
                                                    </>
                                                );
                                            }

                                            return (
                                                <div
                                                    className={
                                                        groupPackId === "standard" ? styles["not-from-pack"] : ""
                                                    }
                                                    key={group}
                                                >
                                                    <Text tag="p" variant="apheleia-body-small" color="midnight-black">
                                                        {renderedPrice}
                                                    </Text>
                                                    <Text
                                                        tag="h3"
                                                        variant="apheleia-list-item-small"
                                                        className={styles["group-title"]}
                                                    >
                                                        <span>
                                                            {group}
                                                            {selectedItemInGroup ? (
                                                                <> - {selectedItemInGroup.name}</>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </span>

                                                        {selectedItemInGroup ? (
                                                            <Badge variant="price">
                                                                <Price
                                                                    price={{
                                                                        ...activeColour.price,
                                                                        discount:
                                                                            selectedItemInGroup.price.discount ?? 0,
                                                                    }}
                                                                    forceCashPrice={
                                                                        !showAccessoriesRate(
                                                                            commonSettings,
                                                                            carConfigSettings,
                                                                        )
                                                                    }
                                                                    showZeroPrice
                                                                />
                                                            </Badge>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </Text>
                                                    <div className={styles.colours}>
                                                        {groupColours.map((colour) => (
                                                            <Button
                                                                data-testid="colour-swatch"
                                                                key={colour.id}
                                                                variant="swatch"
                                                                aria-pressed={activeColour.id === colour.id}
                                                                onClick={onColourClick(colour)}
                                                            >
                                                                <Swatch
                                                                    fill={`#${
                                                                        colour.rgb.substr(0, 2) === "0x"
                                                                            ? colour.rgb.substr(2)
                                                                            : colour.rgb
                                                                    }`}
                                                                    active={activeColour.id === colour.id}
                                                                    image={getCarDBAssetUrl(
                                                                        colour.image,
                                                                        commonSettings.compressedCcisPath,
                                                                        128,
                                                                        128,
                                                                    )}
                                                                />
                                                                <span className={a11y["sr-only"]}>{colour.name}</span>
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </nav>
                    </div>
                </div>
            </Container>
        </SubSectionWrapper>
    );
};

export default Colour;
